import { Breadcrumbs, Typography, Box, Card } from '@mui/material';
import { useI18nContext } from '@procore/core-react';
import React, { useRef } from 'react';
import StyledLink from '../ButtonsLinks/StyledLink';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';
import { useStyles } from './BreadcrumbContainer.styles';
import useHiddenBreadcrumbs from './useHiddenBreadcrumbs';
import basePath from '../../helpers/basePath';
import EmailObfuscationWrapper from '../utils/EmailObfuscationWrapper';

export interface BreadcrumbItem {
  name: string;
  url?: string;
}

interface BreadcrumbContainerProps {
  crumbs: BreadcrumbItem[];
}

const BreadcrumbContainer = ({ crumbs }: BreadcrumbContainerProps) => {
  const I18n = useI18nContext();
  const classes = useStyles();
  const base = I18n.t('components.breadcrumbs.network');
  const ref = useRef(null);
  const {
    showHiddenBreadcrumbs,
    setShowHiddenBreadcrumbs,
    breadcrumbsOverflows,
  } = useHiddenBreadcrumbs(ref);

  return (
    <Breadcrumbs
      className={
        breadcrumbsOverflows
          ? classes.styledBreadcrumbContainerWrap
          : classes.styledBreadcrumbContainer
      }
      ref={ref}
    >
      <AnalyticsWrappedComponent
        data-test-id="base-link"
        componentType={StyledLink}
        eventText="Clicked Breadcrumb"
        eventLabel="Network"
        data-track-click={`Breadcrumb, Navigation, ${base}`}
        href={basePath()}
      >
        <Typography>{base}</Typography>
      </AnalyticsWrappedComponent>
      {breadcrumbsOverflows && (
        <Box style={{ position: 'relative', zIndex: 1 }}>
          <Box
            data-test-id="ellipsis"
            className={classes.ellipsis}
            onClick={() => setShowHiddenBreadcrumbs(!showHiddenBreadcrumbs)}
          >
            &hellip;
          </Box>
          {showHiddenBreadcrumbs && (
            <Card
              data-test-id="hidden-breadcrumbs"
              className={classes.hiddenBreadcrumbs}
            >
              {crumbs
                .filter(
                  (crumb, index) => index !== crumbs.length - 1 && crumb.url
                )
                .map((crumb) => {
                  return (
                    <StyledLink
                      key={crumb.name}
                      href={crumb.url}
                      style={{ margin: '25px 0' }}
                    >
                      <Typography>{crumb.name}</Typography>
                    </StyledLink>
                  );
                })}
            </Card>
          )}
        </Box>
      )}
      {
        // eslint-disable-next-line array-callback-return, consistent-return
        crumbs.map((crumb, index) => {
          if (index === crumbs.length - 1 || !crumb.url) {
            return (
              <Typography
                key={`${crumb.name}-${crumb?.url}`}
                data-test-id="end-breadcrumb"
              >
                <span className={classes.companyName}>
                  <EmailObfuscationWrapper>
                    {crumb.name}
                  </EmailObfuscationWrapper>
                </span>
              </Typography>
            );
          }
          if (!breadcrumbsOverflows) {
            return (
              <AnalyticsWrappedComponent
                data-test-id="link-breadcrumb"
                componentType={StyledLink}
                eventText="Clicked Breadcrumb"
                eventLabel={crumb.name}
                data-track-click={`Breadcrumb, Navigation, ${crumb.name}`}
                href={crumb.url}
                key={`${crumb.name}-${crumb?.url}`}
              >
                <Typography>{crumb.name}</Typography>
              </AnalyticsWrappedComponent>
            );
          }
        })
      }
    </Breadcrumbs>
  );
};

export default BreadcrumbContainer;
